import { bindQuerySelector, jsx, onConnect } from '@amazon/vinyl-tsx'
import { clamp, createDisposer, noop, onAny } from '@amazon/vinyl-util'
import { windowEvents } from '@/util/interaction'
import type { Player } from '@/playback/api'

export function PlaybackScrubBar(props: {
    readonly deps: {
        readonly player: Player
    }
}) {
    const { player } = props.deps
    let scrubbing = false
    let progress = 0

    const view = (
        <div className="progressBarContainer">
            <div
                className="progressBarHit"
                role="slider"
                aria-label="Playback position"
                aria-valuemin={0}
                aria-valuemax={100}
                onmousedown={(event) => {
                    scrubbing = true
                    onProgressBarDown(event.clientX)
                }}
                ontouchstart={(event) => {
                    scrubbing = true
                    const touch = event.touches[0]
                    onProgressBarDown(touch.clientX)
                }}
            >
                <div className="progressBarFills">
                    <div className="progressBarTrack"></div>
                    <div className="progressBarPrefetched"></div>
                    <div className="progressBarFill"></div>
                    <div className="progressHandle"></div>
                </div>
            </div>
        </div>
    )
    const viewEl = bindQuerySelector(view)

    onConnect(view, () => {
        const { add, dispose } = createDisposer()

        add(
            player.on('fetchedRangesChange', () => {
                setPrefetched(player.fetchedTimePercent)
            })
        )

        add(
            onAny(player, ['timeUpdate', 'emptied', 'durationChange'], () => {
                if (!scrubbing) setProgress(player.currentTimePercent)
            })
        )

        add(
            windowEvents.on('touchmove', (event) => {
                if (scrubbing) {
                    const touch = event.touches[0]
                    onProgressBarDown(touch.clientX)
                }
            })
        )

        add(
            windowEvents.on('mouseup', () => {
                scrubDone()
            })
        )

        add(
            windowEvents.on('touchend', (event) => {
                if (event.touches.length === 0) scrubDone()
            })
        )

        add(
            windowEvents.on('mousemove', (event) => {
                if (scrubbing) onProgressBarDown(event.clientX)
            })
        )
        return dispose
    })

    const progressBarHit = viewEl('.progressBarHit')
    const progressBarPrefetched = viewEl('.progressBarPrefetched')
    const progressBarFill = viewEl('.progressBarFill')
    const progressHandle = viewEl('.progressHandle')

    function setProgress(p: number) {
        progress = p
        progressBarFill.style.width = `${p * 100}%`
        progressHandle.style.left = `${p * 100}%`
    }

    function setPrefetched(p: number) {
        progressBarPrefetched.style.width = `${p * 100}%`
    }

    setProgress(0)
    setPrefetched(0)

    function onProgressBarDown(x: number) {
        const rect = progressBarHit.getBoundingClientRect()
        const p = (x - rect.left) / progressBarHit.offsetWidth
        setProgress(clamp(p, 0, 1))
    }

    function scrubDone() {
        if (scrubbing) {
            scrubbing = false
            player.seekTo(progress * player.duration).catch(noop)
        }
    }
    return view
}
